import type { GraphQLContentStatus } from '@confluence/comments-data-manager/entry-points/useAllCommentsDataQueryTypes.ts';
import type { Comment } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { categorizeComments } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type { ProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';

import type {
	ObjectCommentContainerPreloadObjectCommentsQuery$data,
	ObjectCommentContainerPreloadObjectCommentsQuery$variables,
} from '../__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';

// Constants
export const PRELOAD_FOOTER_COMMENTS_COUNT = 3;
export const PRELOAD_LOADING_FLAG = -1;

// Default empty preload data structure (can be spread and isDataProcessed overridden)
const EMPTY_PRELOAD_DATA = {
	allComments: [],
	allFilteredComments: [],
	openInlineComments: [],
	openFooterComments: [],
	resolvedComments: [],
	openComments: [],
	commentsDataMap: { inline: {}, general: {} },
	flatCommentsMap: {},
	hasNestedComments: false,
	commentCount: null,
	hasAnyComments: false,
	isDataProcessed: false,
};

export type UsePreloadAwareObjectCommentsReturn = ProcessedCommentsDataState & {
	isPreloadLoading: boolean;
	isPreloadDataProcessed: boolean;
	isUsingFullData: boolean;
	allDataPreloaded: boolean;
	preloadCompletedWithComments: boolean;
};

/**
 * Builds query variables for preloading footer comments
 * Queries for all content statuses, filtering happens after the fact
 */
export const buildPreloadObjectCommentsQueryVariables = (
	contentId: string,
): ObjectCommentContainerPreloadObjectCommentsQuery$variables => ({
	pageId: contentId,
	// Query for all comments regardless of status (although skip deleted since we have no way to show it), we'll filter on the FE
	contentStatus: ['DRAFT', 'CURRENT', 'ARCHIVED'],
	first: PRELOAD_FOOTER_COMMENTS_COUNT,
	includeCommentReactions: true,
});

/**
 * Calculates if all data has been preloaded
 * Check if loadedCommentCount === totalCount
 */
export const calculateAllDataPreloaded = (
	processedPreloadData: {
		isDataProcessed: boolean;
		allComments: Comment[];
	},
	preloadCacheData: ObjectCommentContainerPreloadObjectCommentsQuery$data | null,
): boolean => {
	if (!processedPreloadData.isDataProcessed || !preloadCacheData?.comments) {
		return false;
	}

	const totalCount = preloadCacheData.comments.totalCount;
	// If totalCount is the loading flag, we're still loading
	if (totalCount === PRELOAD_LOADING_FLAG) {
		return false;
	}

	// Calculate loaded comment count (parent comments + replies)
	const loadedCommentCount =
		preloadCacheData.comments.nodes?.reduce((acc: number, comment) => {
			if (!comment) {
				return acc;
			}
			return acc + (comment?.replies?.length || 0) + 1; // parent + reply count
		}, 0) ?? 0;

	// Check if we've loaded all available comments
	return loadedCommentCount === totalCount;
};

/**
 * Pure function for preload-aware object comments data
 *
 * This function manages both the full comments data (from ProcessedCommentsDataStore) and
 * a preload query for the first 3 footer comments. It returns data from the
 * full store when available, otherwise falls back to the preload query data.
 *
 * @param processedCommentsData - The processed comments data from ProcessedCommentsDataStore
 * @param preloadCacheData - The preload data read from the Relay cache
 * @param contentStatus - The content status filter to apply
 * @returns Object containing comments data, loading states, and preload function
 */
export const getPreloadAwareObjectComments = (
	processedCommentsData: ProcessedCommentsDataState,
	preloadCacheData: ObjectCommentContainerPreloadObjectCommentsQuery$data | null,
	contentStatus: GraphQLContentStatus[],
): UsePreloadAwareObjectCommentsReturn => {
	// Check cache value to determine if we're in a loading state (totalCount === -1)
	const isPreloadLoadingFromCache = preloadCacheData?.comments?.totalCount === PRELOAD_LOADING_FLAG;

	// Process preload data
	// Don't process if we're loading (check cache value, not loading state)
	let processedPreloadData;
	if (isPreloadLoadingFromCache) {
		processedPreloadData = { ...EMPTY_PRELOAD_DATA, isDataProcessed: false };
	} else if (preloadCacheData?.comments) {
		if (preloadCacheData.comments.nodes) {
			// This preloader explicitly opts in to reactions above, so its nodes satisfy the legacy
			// Comment model even though Relay correctly makes conditionally selected fields optional.
			const allComments = (preloadCacheData.comments.nodes || []).filter(
				(node) => node !== null,
			) as Comment[];
			// Filter preload comments by the contentStatus prop (same as CommentsDataBackgroundLoader does)
			// This ensures we only show comments that match the current page's content status
			const categorized = categorizeComments(allComments, contentStatus);

			processedPreloadData = {
				allComments,
				...categorized,
				isDataProcessed: true,
			};
		} else {
			processedPreloadData = { ...EMPTY_PRELOAD_DATA, isDataProcessed: true };
		}
	} else {
		processedPreloadData = { ...EMPTY_PRELOAD_DATA, isDataProcessed: false };
	}

	// Determine which data source to use
	// If ProcessedCommentsDataStore has processed data, use that
	// Otherwise, use the preload query data
	const hasFullData = processedCommentsData.isDataProcessed;

	// Calculate if all data has been preloaded
	const allDataPreloaded = calculateAllDataPreloaded(processedPreloadData, preloadCacheData);

	// Dynamically determine the data to return based on current state
	// isDataProcessed always comes from ProcessedCommentsDataStore
	// Only the comment data itself comes from preload when full data isn't available
	const finalData = hasFullData
		? processedCommentsData
		: {
				...processedCommentsData,
				// Override comment data with preload data, but keep isDataProcessed from store
				...processedPreloadData,
				isDataProcessed: processedCommentsData.isDataProcessed,
				error: processedCommentsData.error,
			};

	return {
		...finalData,
		// Keep separate vars for preload loading status so we don't have to assert isPreloading && ... everywhere
		isPreloadLoading: isPreloadLoadingFromCache,
		isPreloadDataProcessed: processedPreloadData.isDataProcessed,
		// Indicate whether preload completed with comments (for SSR placeholder display)
		preloadCompletedWithComments:
			processedPreloadData.isDataProcessed && processedPreloadData.allFilteredComments.length > 0,
		// Indicate whether the preload contained ALL comments
		allDataPreloaded,
		// Indicate whether we're currently using full data or preload data
		isUsingFullData: hasFullData,
	};
};
